import { handoffRequestKey, completeHandoffRequest } from "@/lib/handoff-request";
import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { KnowledgeCitations, type KnowledgeCitation } from "@/components/knowledge-citations";
import { getMessageLangMeta, prefersReducedMotion } from "@/lib/a11y";
import { advancePreviewAnnouncer, initialPreviewAnnouncerState, shouldRenderAnnouncer, type PreviewAnnouncerState } from "@/lib/a11y-announcer";
import { createSendGuard } from "@/lib/send-guard";
import { isNearBottom, scrollAnchorIntoView } from "@/lib/scroll";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send, Bot, User, Loader2, ArrowDown, HelpCircle, Sparkles, MessageCircle, ChevronRight, UserCog, RotateCcw, Wrench, Info, Headset, PhoneOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PreChatForm, { type PreChatContact } from "@/components/prechat-form";
import { SelectGroup, SelectLabel } from "@/components/ui/select";
import type { Chatbot } from "@shared/schema";
import { friendlyErrorMessage } from "@/lib/queryClient";
import { ROLES, type Role } from "@shared/schema";
import { useAuth } from "@/lib/auth";
import { resolveThemeWithContrast, readableTextOn } from "@/lib/themes";
import { CHAT_LANGUAGES, AUTO_LANGUAGE_HELP } from "@/lib/chat-languages";
import { diagRecordAnnouncement } from "@/lib/a11y-diagnostics";

interface PredefinedQuestion {
  id: string;
  question: string;
  category?: string;
}

interface ChatMessage {
  citations?: KnowledgeCitation[];
  // "agent" = a human live-chat operator; "system" = status/notice line.
  role: "user" | "assistant" | "agent" | "system";
  content: string;
  id?: string;
  /** Essential error/status message (WCAG 4.1.3): announced even when the
      ADA/WCAG narration toggle is OFF. */
  isError?: boolean;
}

interface OrgUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  department?: string | null;
  jobTitle?: string | null;
  active?: string | null;
}

interface BotTool {
  id: string;
  name: string;
  description?: string | null;
  allowedRoles?: string[] | null;
  enabled: boolean;
  isActive?: string | null;
}

// Open markdown links (e.g. admission-form download links) in a new tab so
// a click opens/downloads the file without leaving the chat.
const markdownComponents = {
  a: ({ node, children, ...props }: any) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
    >
      {children}
    </a>
  ),
};

const SESSION_USER_VALUE = "__session__";
const NO_ROLE_OVERRIDE_VALUE = "__none__";
// Sentinel sent on the wire as `as_role: "public"`. The server treats
// this specifically — it suppresses the invoker entirely so the chat
// runs as if no one is signed in (matches a `public` accessMode bot
// being hit by an anonymous visitor). It is NOT a member of the ROLES
// enum so it can never reach the executor's allowedRoles ACL check.
const PUBLIC_PREVIEW_ROLE = "public";

// Friendly metadata for every role in the system. Keys must match the
// canonical ROLES enum exactly so the override picker stays in sync with
// what the server validates against. The "group" buckets the role under
// either Workforce (regular seats) or Admin (privileged seats) in the
// dropdown so it's obvious which personas have elevated access.
const ROLE_META: Record<Role, { label: string; group: "workforce" | "admin"; description: string }> = {
  user:        { label: "User",            group: "workforce", description: "Generic seat. Treat like a regular employee with no extra privileges." },
  employee:    { label: "Employee",        group: "workforce", description: "Regular staff member. Sees only employee-facing tools and answers." },
  manager:     { label: "Manager",         group: "workforce", description: "People manager. Can see manager-only HR/IT tools (e.g. team data)." },
  admin:       { label: "Admin",           group: "admin",     description: "General admin. Can manage the bot and call admin-gated tools." },
  org_admin:   { label: "Org Admin",       group: "admin",     description: "Owner of the organization. Highest level of access in this tenant." },
  super_admin: { label: "Super Admin",     group: "admin",     description: "Platform-level admin. Can do anything an Org Admin can, and more." },
  hr_admin:    { label: "HR Admin",        group: "admin",     description: "HR systems admin. Can call HRMS-restricted tools (PTO, payroll, etc)." },
  it_admin:    { label: "IT Admin",        group: "admin",     description: "IT systems admin. Can call ITSM-restricted tools (tickets, access)." },
  bot_admin:   { label: "Bot Admin",       group: "admin",     description: "Manages chatbots, tools and integrations. Cannot manage org users." },
  // Live chat roles. These users do NOT see the rest of the admin app
  // — they're scoped to the Live Chat console only. Listed here so the
  // role-preview dropdown can render them.
  live_admin:  { label: "Live Chat Admin", group: "admin",     description: "Owns Live Chat: settings, offline messages, and full operator oversight." },
  live_power:  { label: "Live Chat Lead",  group: "admin",     description: "Live chat supervisor. Claims queued sessions and oversees other operators." },
  live_agent:  { label: "Live Agent",      group: "workforce", description: "Front-line live chat operator. Claims chats from the queue and replies to visitors." },
};

// "Public User" is rendered alongside the canonical ROLES but is NOT a
// member of the ROLES enum — it's a preview-only sentinel that maps to
// "no signed-in user" on the server.
const PUBLIC_ROLE_META = {
  label: "Public User",
  group: "external" as const,
  description: "Anonymous visitor with no session. Sees only the bot's prompt — no tools, no internal data.",
};

function getRoleMeta(role: string): { label: string; group: "workforce" | "admin" | "external"; description: string } {
  if (role === PUBLIC_PREVIEW_ROLE) return PUBLIC_ROLE_META;
  return ROLE_META[role as Role] ?? { label: role, group: "workforce" as const, description: "Custom role." };
}

// Mirror of the server's tool ACL check (server/tools/executor.ts
// callerHasAllowedRole): a tool is callable by a role when:
//   - tool itself is enabled+active on this bot
//   - AND allowedRoles is empty/missing (open to everyone)
//     OR the role appears in the list
//     OR the role is super_admin (explicit bypass on the server)
// We use this purely for the in-preview "N tools available as <role>"
// readout — the server is still the source of truth at call time.
function isToolAvailableForRole(tool: BotTool, role: string): boolean {
  if (!tool.enabled) return false;
  if (tool.isActive === "false") return false;
  if (role === "super_admin") return true;
  const allowed = tool.allowedRoles ?? [];
  if (allowed.length === 0) return true;
  return allowed.includes(role);
}

export default function ChatPreview({ chatbot }: { chatbot: Chatbot }) {
  const { user: sessionUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: "assistant", content: chatbot.welcomeMessage || "Hello! How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous double-send guard (lib/send-guard.ts, unit-tested): the input
  // is intentionally never disabled while loading, so rapid Enter presses
  // must be blocked here rather than by the disabled attribute.
  const sendGuardRef = useRef(createSendGuard());
  // Screen-reader announcements (WCAG 4.1.3): concise typing status plus each
  // completed assistant/agent reply exactly once — never the whole thread.
  // Gated by the org-level ADA/WCAG compliance toggle (a11yAnnouncements from
  // the public bot config); structural accessibility remains always on.
  const { data: publicBotConfig } = useQuery<{ a11yAnnouncements?: boolean }>({
    queryKey: [`/api/v1/chatbots/${chatbot.apiKey}/config`],
    // The compliance toggle must take effect without a full page reload:
    // treat this config as short-lived instead of cached forever.
    staleTime: 30_000,
  });
  const announceEnabled = !!publicBotConfig?.a11yAnnouncements;
  const [srAnnouncement, setSrAnnouncement] = useState("");
  // Pure toggle-aware announcer (lib/a11y-announcer.ts, unit-tested):
  // ON → announce-once machine; OFF → pending narration cancelled, essential
  // errors still announce once; OFF→ON → re-baselines (no history re-read).
  const announcerRef = useRef<PreviewAnnouncerState>(initialPreviewAnnouncerState);
  useEffect(() => {
    const next = advancePreviewAnnouncer(announcerRef.current, {
      enabled: announceEnabled,
      messages,
      isLoading,
    });
    announcerRef.current = next;
    setSrAnnouncement(next.announcement);
    if (announceEnabled) diagRecordAnnouncement(next.announcement, "admin chat preview announcer");
  }, [messages, isLoading, announceEnabled]);
  // Live-agent handoff state. When `live` is set, the preview behaves
  // exactly like the public widget: it stops talking to /api/v1/chat and
  // routes the visitor's messages to the queued/claimed LiveChatSession,
  // so testing in settings exercises the same end-to-end handoff that a
  // real public visitor gets.
  const [handoffPending, setHandoffPending] = useState(false);
  // Departments for routing a handoff. Fetched with the admin's credentials
  // (this preview lives in settings) even though the handoff POST itself is
  // anonymous, so testers can verify department routing end-to-end.
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [handoffDept, setHandoffDept] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    fetch("/api/departments/list", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled && Array.isArray(d)) setDepartments(d); })
      .catch(() => { /* no departments */ });
    return () => { cancelled = true; };
  }, []);
  const [live, setLive] = useState<{
    sessionId: string;
    visitorToken: string;
    status: "queued" | "active" | "closed";
    position: number | null;
    operatorName: string | null;
    knownMessageIds: Set<string>;
  } | null>(null);
  // Mirror of `live` for reading the latest committed value inside the queued
  // no-agent timeout without re-subscribing it on every state change.
  const liveRef = useRef<typeof live>(null);
  liveRef.current = live;
  // "Preview as" lets an admin impersonate any seat in the org and/or
  // override the effective role, so role-gated tools can be sanity-checked
  // without logging in as each employee. Server enforces admin-only.
  // Empty string = "use my own dashboard session" (the legacy behaviour).
  const [asUserId, setAsUserId] = useState<string>("");
  const [asRole, setAsRole] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [selectedChannel, setSelectedChannel] = useState<string>("web");
  // Simulated user JSON: freeform object that merges into the chat POST body
  // (only server-whitelisted keys like user_name and user_email are honoured).
  // Invalid JSON shows a safe inline error; the send button stays active so
  // the admin can still test without the context field.
  const [simulatedUserJson, setSimulatedUserJson] = useState<string>("");
  const [simulatedUserJsonError, setSimulatedUserJsonError] = useState<string | null>(null);
  // Explicit session nonce — incremented by "Reset session" so the
  // previewSessionId changes even when persona/role stay the same.
  // This forces a brand-new server-side conversation every time.
  const [sessionNonce, setSessionNonce] = useState<number>(0);
  // Public bots make the pre-chat contact form mandatory. Mirror that in the
  // preview so admins see (and test) the same gate a real visitor hits before
  // chatting — and so the lead exists that the server now requires.
  const [preChatDone, setPreChatDone] = useState(false);
  const [preContact, setPreContact] = useState<PreChatContact | null>(null);
  // Two-step preview: admins first pick the role/persona they want to preview
  // as, then the bot screen is shown governed by that role's rules (tool ACLs,
  // and the mandatory pre-chat gate for public bots). Off = role-selection step.
  const [previewStarted, setPreviewStarted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // WCAG 1.4.3: bubble text colors are clamped to >= 4.5:1 against the
  // admin-configured bubble backgrounds (hue-preserving lightness nudge).
  const { theme } = resolveThemeWithContrast(chatbot as any);

  const { data: predefinedQuestions } = useQuery<PredefinedQuestion[]>({
    queryKey: ["/api/chatbots", chatbot.id, "predefined-questions"],
  });

  // Pull the org's seats so the admin can pick one to impersonate. The
  // endpoint is admin-gated, so for non-admin previewers it 401s and we
  // simply hide the picker (the chat still works as their own session).
  const { data: orgUsers } = useQuery<OrgUser[]>({
    queryKey: ["/api/org/users"],
  });

  // Pull the bot's tool inventory (with allowedRoles + enabled state) so
  // the picker can show "X tools available as <role>" in real time. This
  // makes the abstract idea of "role" tangible — switching the override
  // immediately changes the count, which is exactly the bot capability
  // surface for that user type.
  const { data: botTools } = useQuery<BotTool[]>({
    queryKey: ["/api/chatbots", chatbot.id, "tools"],
  });

  // Resolve which role we're currently previewing as, so we can compute
  // the tool-availability readout. Precedence mirrors the server's
  // /api/v1/chat resolution:
  //   1. explicit "Public User" override               — server: invoker suppressed entirely
  //   2. explicit role override (asRole)               — server: roleOverride
  //   3. role of the impersonated org user (asUserId)  — server: invokerUser.role
  //   4. otherwise the dashboard session user's role   — server: req.session user
  const isPublicPreview = asRole === PUBLIC_PREVIEW_ROLE;
  const impersonatedUser = asUserId ? orgUsers?.find((u) => u.id === asUserId) : undefined;
  const effectiveRole = isPublicPreview
    ? PUBLIC_PREVIEW_ROLE
    : (asRole || impersonatedUser?.role || sessionUser?.role || "");
  const effectiveRoleMeta = effectiveRole ? getRoleMeta(effectiveRole) : null;
  // Which "source" the effective role came from — drives the summary copy
  // ("Acting as" vs "Previewing as your own role") so the user always
  // knows whether they're seeing the impersonation result or the default.
  const effectiveRoleSource: "public" | "override" | "impersonated" | "session" | "none" =
    isPublicPreview ? "public"
      : asRole ? "override"
      : impersonatedUser ? "impersonated"
      : sessionUser?.role ? "session"
      : "none";
  // Public users get NO tools — the server forces invokerUser=null which
  // makes chatWithBot skip tool advertising entirely. Don't bother running
  // the per-role ACL filter; just report 0 so the UI matches reality.
  const availableToolCount = isPublicPreview
    ? 0
    : (effectiveRole && botTools
      ? botTools.filter((t) => isToolAvailableForRole(t, effectiveRole)).length
      : 0);
  const totalEnabledToolCount = botTools?.filter((t) => t.enabled && t.isActive !== "false").length ?? 0;

  // Reset the in-memory transcript whenever the impersonated identity
  // changes, otherwise the new "user" inherits the prior user's history
  // for this preview session and the LLM gets confused about who's
  // asking. Also reset the bound session_id so server-side conversation
  // state doesn't carry over either.
  // Nonce is appended so "Reset session" forces a new server-side conversation
  // even when the persona/role haven't changed.
  const previewSessionId = `preview-${chatbot.id}-${asUserId || "self"}-${asRole || "default"}-${sessionNonce}`;
  // Public chatbots require a consented lead before any message; show the same
  // mandatory contact gate in the preview until it's completed for this session.
  const isPublicBot = ((chatbot as any).accessMode ?? "authenticated") === "public";
  const needsPreChat = isPublicBot && !preChatDone;
  const resetTranscript = () => {
    setMessages([{ role: "assistant", content: chatbot.welcomeMessage || "Hello! How can I help you today?" }]);
    // The session id is derived from the persona, so switching persona means a
    // brand-new session with no lead yet — re-show the contact gate.
    setPreChatDone(false);
    setPreContact(null);
    // Tear down any in-flight live session too, otherwise switching
    // "Preview as" clears the transcript but the composer keeps routing
    // to the old visitor session (cross-persona leak). Fire-and-forget
    // close the session, then drop back to AI mode.
    setLive((prev) => {
      if (prev && prev.status !== "closed") {
        fetch(`/api/live-chat/visitor/${prev.sessionId}/close`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          body: JSON.stringify({ visitorToken: prev.visitorToken }),
        }).catch(() => {/* swallow — best effort */});
      }
      return null;
    });
  };

  // ── Adaptive auto-scroll (WCAG 1.4.10 / 400% zoom safe) ────────
  // scrollRef points at the Radix ScrollArea ROOT (overflow hidden); the
  // actual scroll owner is its Viewport. lib/scroll.ts also handles the
  // document-owns-scrolling case at 300–400% zoom.
  const getViewport = () =>
    (scrollRef.current?.querySelector("[data-radix-scroll-area-viewport]") as HTMLElement | null) ??
    scrollRef.current;
  const latestUserMsgRef = useRef<HTMLDivElement | null>(null);
  const latestBotMsgRef = useRef<HTMLDivElement | null>(null);
  const nearBottomRef = useRef(true);
  const [newReplyPending, setNewReplyPending] = useState(false);
  // Attach the near-bottom tracker to whichever viewport is currently
  // mounted (the ScrollArea unmounts behind pre-chat gates). Runs every
  // render but is idempotent: it re-binds (with cleanup) only when the
  // viewport element instance actually changes. Also tracks WINDOW scroll:
  // at 300–400% zoom the document can own vertical scrolling, and reading
  // history there must suppress reply auto-scroll too.
  const scrollTrackedEl = useRef<HTMLElement | null>(null);
  const detachScrollTracker = useRef<(() => void) | null>(null);
  useEffect(() => {
    const vp = getViewport();
    if (!vp || scrollTrackedEl.current === vp) return;
    detachScrollTracker.current?.();
    scrollTrackedEl.current = vp;
    const onScroll = () => {
      nearBottomRef.current = isNearBottom(vp);
      if (nearBottomRef.current) setNewReplyPending(false);
    };
    vp.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    detachScrollTracker.current = () => {
      vp.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScroll);
    };
  });
  // Unbind everything when the preview unmounts.
  useEffect(() => () => { detachScrollTracker.current?.(); }, []);
  const scrollToLatest = (anchor: HTMLElement | null) => {
    const vp = getViewport();
    if (!vp) return;
    scrollAnchorIntoView(vp, anchor, prefersReducedMotion());
    nearBottomRef.current = true;
    setNewReplyPending(false);
  };
  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    const own = last.role === "user";
    // Always bring the user's own message into view; follow replies only
    // when the reader hasn't scrolled up to read history — otherwise show
    // the "New reply" chip instead of yanking them down.
    if (!own && !nearBottomRef.current) {
      setNewReplyPending(true);
      return;
    }
    const anchor = own
      ? latestUserMsgRef.current
      : last.role === "system"
        ? null
        : latestBotMsgRef.current;
    requestAnimationFrame(() => scrollToLatest(anchor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
  const lastUserIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "user") return i;
    return -1;
  })();
  const lastBotIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const r = messages[i].role;
      if (r === "assistant" || r === "agent") return i;
    }
    return -1;
  })();

  const sendMessage = async (text?: string) => {
    const userMessage = (text || input).trim();
    // `isLoading` is React state and goes stale within a render frame — two
    // rapid Enter presses (the input stays ENABLED while a reply is pending)
    // would both pass it. The synchronous send guard blocks the duplicate.
    // Block send when the simulated-user JSON field has a validation error.
    // The error is already shown inline; return before touching state so no
    // partial message appears and no /api/v1/chat request is issued.
    if (simulatedUserJsonError) return;
    if (!userMessage || isLoading || !sendGuardRef.current.begin()) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      // Parse the simulated user JSON. Error state is cleared when the field
      // is valid, so reaching here guarantees the JSON is well-formed or empty.
      let simUserCtx: Record<string, unknown> = {};
      if (simulatedUserJson.trim()) {
        simUserCtx = JSON.parse(simulatedUserJson);
      }

      const res = await fetch("/api/v1/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${chatbot.apiKey}`,
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: previewSessionId,
          // Server only honours these when the dashboard caller is an org
          // admin — non-admins silently fall back to their own session.
          ...(asUserId ? { as_user_id: asUserId } : {}),
          ...(asRole ? { as_role: asRole } : {}),
          ...(selectedLanguage ? { language: selectedLanguage } : {}),
          channel: selectedChannel || "web",
          // Simulated user context (optional). Object is spread so
          // server-recognised keys (user_name, user_email) are at the
          // top level; unrecognised keys are silently ignored.
          ...simUserCtx,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        // Quota exhausted — surface the friendly limit message verbatim
        // (no "check your API key" suffix; this is not a config problem).
        if (res.status === 429) {
          let body: any = null;
          try { body = JSON.parse(errorText); } catch { /* non-JSON */ }
          if (body?.code === "MESSAGE_LIMIT_REACHED") {
            setMessages((prev) => [
              ...prev,
              { role: "assistant", isError: true, content: `${body.title ?? "Monthly Message Limit Reached"}\n\n${body.message ?? "Your organization has reached the monthly message limit for the current plan. Please upgrade your plan or contact your administrator to continue using the chatbot."}` },
            ]);
            return;
          }
        }
        throw new Error(friendlyErrorMessage(res.status, errorText));
      }

      const data = await res.json();
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, citations: data.citations }]);
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", isError: true, content: `Error: ${err.message}. Make sure your OpenAI API key is configured in Settings.` },
      ]);
    } finally {
      sendGuardRef.current.end();
      setIsLoading(false);
      // Accessibility-specific refocus after a response: only when the org
      // ADA/WCAG toggle is ON. With it OFF the input is never disabled, so
      // focus is preserved naturally and no programmatic move happens.
      if (announceEnabled) inputRef.current?.focus();
    }
  };

  const liveActive = !!live && live.status !== "closed";

  // Escalate this preview conversation to a human. Mirrors the public
  // widget's requestHandoff: pre-flight the availability check, then POST
  // /api/live-chat/handoff (anonymous — credentials omitted so it lands as
  // a normal visitor session, not a privileged dashboard call) and switch
  // into live mode.
  const requestHandoff = async () => {
    if (!chatbot.apiKey || handoffPending || live) return;
    setHandoffPending(true);
    // Always escalate to the platform queue, even when no operator is online
    // — the session is created queued ("handshake to the platform"). If
    // nobody claims it within the no-agent window, the queued-timeout effect
    // tells the visitor their details were captured for follow-up.
    const su = sessionUser as any;
    // Mirror the public widget: the handoff should carry the identity of
    // whoever is being previewed, not the logged-in admin. Previewing as a
    // "Public User" hands off anonymously (no name/email) exactly like a
    // real anonymous visitor; previewing as a specific seat carries that
    // seat's name/email; otherwise fall back to the dashboard session user.
    const persona: any = isPublicPreview ? null : (impersonatedUser ?? su);
    const visitorName = (persona
      ? (persona.fullName ?? persona.name ?? persona.email ?? "Preview tester")
      : null) ?? preContact?.name ?? preContact?.email ?? null;
    const visitorEmail = (persona?.email ?? null) ?? preContact?.email ?? null;
    try {
      const res = await fetch("/api/live-chat/handoff", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": handoffRequestKey(previewSessionId) },
        credentials: "omit",
        body: JSON.stringify({
          apiKey: chatbot.apiKey,
          sessionId: previewSessionId,
          visitorName,
          visitorEmail,
          departmentId: handoffDept || null,
          reason: "Preview test \u2014 Talk to a person",
        }),
      });
      if (!res.ok) throw new Error("Handoff failed");
      const sess = await res.json();
      setLive({
        sessionId: sess.id,
        visitorToken: sess.visitorToken ?? "",
        status: sess.status,
        position: null,
        operatorName: null,
        knownMessageIds: new Set<string>(),
      });
      setMessages((prev) => [
        ...prev,
        { role: "system", content: "Connecting you to a live agent\u2026" },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "system",
          isError: true,
          content: "Couldn't reach a live agent right now. Please try again in a moment.",
        },
      ]);
    } finally {
      setHandoffPending(false);
    }
  };

  // Poll the live session every 2s: pull operator/system messages, queue
  // position and operator name. Visitor's own messages are added
  // optimistically, so we filter sender==="visitor" out of the merge.
  useEffect(() => {
    if (!live || live.status === "closed") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(
          `/api/live-chat/visitor/${live.sessionId}/state`,
          { credentials: "omit", headers: { "X-Visitor-Token": live.visitorToken } },
        );
        if (!res.ok) return;
        const state = (await res.json()) as {
          status: "queued" | "active" | "closed";
          position: number | null;
          operatorName: string | null;
          messages: Array<{ id: string; sender: string; content: string; createdAt: string }>;
        };
        if (cancelled) return;
        setLive((prev) => {
          if (!prev) return prev;
          const fresh = state.messages.filter(
            (m) => !prev.knownMessageIds.has(m.id) && m.sender !== "visitor",
          );
          if (fresh.length > 0) {
            const newMsgs: ChatMessage[] = fresh.map((m) => ({
              id: m.id,
              role: m.sender === "operator" ? "agent" : "system",
              content: m.content,
            }));
            setMessages((prevMsgs) => [...prevMsgs, ...newMsgs]);
          }
          if (prev.status !== "active" && state.status === "active" && state.operatorName) {
            setMessages((prevMsgs) => [
              ...prevMsgs,
              { role: "system", content: `${state.operatorName} joined the chat.` },
            ]);
          }
          if (prev.status !== "closed" && state.status === "closed") {
            setMessages((prevMsgs) => [
              ...prevMsgs,
              { role: "system", content: "Human chat ended. Choose Continue with AI when ready." },
            ]);
          }
          const nextKnown = new Set(prev.knownMessageIds);
          state.messages.forEach((m) => nextKnown.add(m.id));
          return {
            ...prev,
            status: state.status,
            position: state.position,
            operatorName: state.operatorName ?? prev.operatorName,
            knownMessageIds: nextKnown,
          };
        });
      } catch {
        /* ignore polling failures */
      }
    };
    tick();
    const i = window.setInterval(tick, 2000);
    window.addEventListener("focus", tick);
    window.addEventListener("online", tick);
    return () => {
      cancelled = true;
      window.clearInterval(i);
      window.removeEventListener("focus", tick);
      window.removeEventListener("online", tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.sessionId, live?.status]);

  // No-agent fallback: if the visitor stays queued for 2 minutes without an
  // operator claiming the session, tell them their details were captured for
  // follow-up, then drop back to AI bot mode. The queued session stays in the
  // platform queue so an agent can still pick it up offline.
  useEffect(() => {
    if (!live || live.status !== "queued") return;
    const queuedId = live.sessionId;
    const t = window.setTimeout(() => {
      // Re-validate against the latest committed state: an operator may have
      // claimed the session (status flipped to active) in the same tick the
      // timer fired, or a new session may have replaced this one.
      const cur = liveRef.current;
      if (!cur || cur.sessionId !== queuedId || cur.status !== "queued") return;
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Unfortunately, no live agents are available right now. Your information has been sent, and someone will contact you soon.",
        },
      ]);
      setLive(null);
    }, 120000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.sessionId, live?.status]);

  // Closure ends the human session; AI continuation requires an explicit action.
  useEffect(() => {
    if (live?.status === "closed") {
      completeHandoffRequest(previewSessionId);
    }
  }, [live?.status]);

  const sendLiveMessage = async (text: string) => {
    if (!live) return;
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    try {
      await fetch(`/api/live-chat/visitor/${live.sessionId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ visitorToken: live.visitorToken, content: text }),
      });
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "system", isError: true, content: "Couldn't deliver your message. Please try again." },
      ]);
    } finally {
      // Same rule as the AI path: programmatic refocus is ADA-gated.
      if (announceEnabled) inputRef.current?.focus();
    }
  };

  const endLiveChat = async () => {
    if (!live) return;
    try {
      const response = await fetch(`/api/live-chat/visitor/${live.sessionId}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ visitorToken: live.visitorToken }),
      });
      if (!response.ok) throw new Error("Close rejected");
      window.dispatchEvent(new Event("focus"));
    } catch {
      setMessages(previous => [...previous, { role: "system", isError: true,
        content: "Could not end the human chat. Please try End live chat again." }]);
    }
  };

  const submitInput = () => {
    if (live?.status === "closed") return;
    const text = input.trim();
    if (!text) return;
    if (liveActive) {
      sendLiveMessage(text);
    } else {
      sendMessage();
    }
  };

  const showSuggestions = messages.length <= 1 && predefinedQuestions && predefinedQuestions.length > 0;

  return (
    <Card className="max-w-2xl mx-auto overflow-hidden" style={{ borderRadius: theme.borderRadius }}>
      <CardHeader className="border-b" style={{ backgroundColor: theme.headerBg, color: theme.headerText }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: theme.primaryColor, color: readableTextOn(theme.primaryColor) }}>
            <Bot className="w-4 h-4" />
          </div>
          <CardTitle className="text-sm" style={{ color: theme.headerText }}>{chatbot.name} - Preview</CardTitle>
        </div>
      </CardHeader>
      {!previewStarted && (
        <div
          className="px-4 py-3 border-b bg-muted/40 space-y-3 text-xs"
          data-testid="panel-preview-as"
        >
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex w-full items-center gap-1.5 text-foreground font-semibold mr-1 text-sm">
              <UserCog className="w-4 h-4" />
              <span>Who are you previewing as?</span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="a11y-tip a11y-tip-below ml-0.5 inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                    aria-label="What does Preview as do?"
                    data-tip="What does Preview as do?"
                    data-testid="button-preview-as-help"
                  >
                    <Info className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  className="w-80 text-xs leading-relaxed"
                  data-testid="popover-preview-as-help"
                >
                  <p className="font-semibold mb-1.5 text-sm">What is a role?</p>
                  <p className="text-muted-foreground mb-2">
                    Every user in your organization has a role (e.g. Employee, Manager, HR Admin). Each tool you wire into this bot can be restricted to specific roles, so different users get different capabilities.
                  </p>
                  <p className="font-semibold mb-1 text-sm">Why preview as another role?</p>
                  <p className="text-muted-foreground">
                    Pick a seat or override the role to see exactly what that user would experience in this bot — which tools they can call, and what answers they get back.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            {orgUsers && orgUsers.length > 0 && (
            <Select
              value={asUserId || SESSION_USER_VALUE}
              onValueChange={(v) => {
                setAsUserId(v === SESSION_USER_VALUE ? "" : v);
                resetTranscript();
              }}
            >
              <SelectTrigger
                className="h-7 w-[220px] text-xs"
                aria-label="Preview as user"
                data-testid="select-preview-user"
              >
                <SelectValue placeholder="Your session" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SESSION_USER_VALUE} data-testid="option-preview-user-self">
                  Your session (default)
                </SelectItem>
                {orgUsers
                  .filter((u) => u.active !== "false")
                  .sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email))
                  .map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`option-preview-user-${u.id}`}>
                      {(u.name ?? u.email)}
                      <span className="text-muted-foreground"> · {getRoleMeta(u.role).label}</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            )}
            <Select
              value={asRole || NO_ROLE_OVERRIDE_VALUE}
              onValueChange={(v) => {
                const next = v === NO_ROLE_OVERRIDE_VALUE ? "" : v;
                setAsRole(next);
                // "Public User" means anonymous — clear any impersonated
                // seat so the picker doesn't lie about what the chat will
                // run as. (The server would suppress the seat anyway.)
                if (next === PUBLIC_PREVIEW_ROLE) {
                  setAsUserId("");
                }
                resetTranscript();
              }}
            >
              <SelectTrigger
                className="h-7 w-[180px] text-xs"
                aria-label="Preview role override"
                data-testid="select-preview-role"
              >
                <SelectValue placeholder="Use user's role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ROLE_OVERRIDE_VALUE} data-testid="option-preview-role-none">
                  Use user's role
                </SelectItem>
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">External</SelectLabel>
                  <SelectItem value={PUBLIC_PREVIEW_ROLE} data-testid="option-preview-role-public">
                    Public User
                  </SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Workforce</SelectLabel>
                  {ROLES.filter((r) => ROLE_META[r].group === "workforce").map((r) => (
                    <SelectItem key={r} value={r} data-testid={`option-preview-role-${r}`}>
                      {ROLE_META[r].label}
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Admin</SelectLabel>
                  {ROLES.filter((r) => ROLE_META[r].group === "admin").map((r) => (
                    <SelectItem key={r} value={r} data-testid={`option-preview-role-${r}`}>
                      {ROLE_META[r].label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {(asUserId || asRole) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  setAsUserId("");
                  setAsRole("");
                  resetTranscript();
                }}
                data-testid="button-preview-as-reset"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset
              </Button>
            )}
          </div>
          {effectiveRoleMeta && effectiveRoleSource !== "none" && (
            <div
              className="flex flex-wrap items-center gap-2 pl-5"
              data-testid="panel-preview-role-summary"
            >
              <Badge variant="secondary" className="text-[10px] font-medium" data-testid={`badge-effective-role-${effectiveRole}`}>
                {effectiveRoleSource === "session" && `Previewing as your role: ${effectiveRoleMeta.label}`}
                {effectiveRoleSource === "public" && `Acting as ${effectiveRoleMeta.label} (anonymous)`}
                {(effectiveRoleSource === "override" || effectiveRoleSource === "impersonated") && `Acting as ${effectiveRoleMeta.label}`}
              </Badge>
              {botTools !== undefined && (
                <Badge variant="outline" className="text-[10px] font-medium gap-1" data-testid="badge-tools-available">
                  <Wrench className="w-3 h-3" />
                  {availableToolCount} of {totalEnabledToolCount} tool{totalEnabledToolCount === 1 ? "" : "s"} available
                </Badge>
              )}
              <span className="text-[11px] text-muted-foreground" data-testid="text-effective-role-description">
                {effectiveRoleMeta.description}
              </span>
            </div>
          )}
          {/* ── Channel selector ─────────────────────────────────── */}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[11px] text-muted-foreground shrink-0">Channel:</span>
            <Select
              value={selectedChannel}
              onValueChange={(v) => {
                setSelectedChannel(v);
                resetTranscript();
              }}
            >
              <SelectTrigger
                className="h-7 w-[140px] text-xs"
                aria-label="Preview channel"
                data-testid="select-preview-channel"
              >
                <SelectValue placeholder="web" />
              </SelectTrigger>
              <SelectContent>
                {(["web", "sms", "email", "mobile", "api"] as const).map((ch) => (
                  <SelectItem key={ch} value={ch} data-testid={`option-preview-channel-${ch}`}>
                    {ch}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* ── Simulated user JSON ───────────────────────────────── */}
          <div className="flex flex-col gap-1 mt-1">
            <label className="text-[11px] text-muted-foreground font-medium" htmlFor="sim-user-json">
              Simulated user context (JSON, optional)
            </label>
            <textarea
              id="sim-user-json"
              rows={3}
              className={`w-full rounded-md border bg-background px-2 py-1 text-xs font-mono resize-none focus:outline-none focus:ring-1 focus:ring-ring ${simulatedUserJsonError ? "border-destructive focus:ring-destructive" : ""}`}
              placeholder='{ "user_name": "Jane Doe", "user_email": "jane@example.com" }'
              value={simulatedUserJson}
              data-testid="input-sim-user-json"
              onChange={(e) => {
                const raw = e.target.value;
                setSimulatedUserJson(raw);
                if (!raw.trim()) {
                  setSimulatedUserJsonError(null);
                  return;
                }
                try {
                  const parsed = JSON.parse(raw);
                  if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null)
                    setSimulatedUserJsonError("Must be a JSON object, e.g. { \"user_name\": \"...\" }");
                  else
                    setSimulatedUserJsonError(null);
                } catch {
                  setSimulatedUserJsonError("Invalid JSON — check quotes, commas, and brackets.");
                }
              }}
            />
            {simulatedUserJsonError && (
              <p className="text-[10px] text-destructive" role="alert" data-testid="error-sim-user-json">
                {simulatedUserJsonError}
              </p>
            )}
          </div>
          <Button
            className="w-full mt-1"
            size="sm"
            style={{ backgroundColor: theme.primaryColor, color: readableTextOn(theme.primaryColor) }}
            onClick={() => {
              resetTranscript();
              setPreviewStarted(true);
            }}
            data-testid="button-start-preview"
          >
            Start preview
          </Button>
        </div>
      )}
      {previewStarted && (
        <div
          className="px-4 py-2 border-b bg-muted/40 flex flex-wrap items-center gap-2 text-xs"
          data-testid="panel-preview-active-role"
        >
          {effectiveRoleMeta && effectiveRoleSource !== "none" ? (
            <>
              <Badge variant="secondary" className="text-[10px] font-medium" data-testid={`badge-active-role-${effectiveRole}`}>
                {effectiveRoleSource === "session" && `Previewing as your role: ${effectiveRoleMeta.label}`}
                {effectiveRoleSource === "public" && `Acting as ${effectiveRoleMeta.label} (anonymous)`}
                {(effectiveRoleSource === "override" || effectiveRoleSource === "impersonated") && `Acting as ${effectiveRoleMeta.label}`}
              </Badge>
              {botTools !== undefined && (
                <Badge variant="outline" className="text-[10px] font-medium gap-1" data-testid="badge-active-tools-available">
                  <Wrench className="w-3 h-3" />
                  {availableToolCount} of {totalEnabledToolCount} tool{totalEnabledToolCount === 1 ? "" : "s"} available
                </Badge>
              )}
            </>
          ) : (
            <span className="text-[11px] text-muted-foreground" data-testid="text-active-role-default">
              Previewing as your default session
            </span>
          )}
          {/* Reset session — creates a brand-new conversation (new session_id
              via the nonce) without changing persona or role. Preview state
              never leaks: the nonce is part of previewSessionId which always
              starts with "preview-", so the server always marks IsPreview=true. */}
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => {
              setSessionNonce((n) => n + 1);
              resetTranscript();
            }}
            data-testid="button-reset-session"
            title="Clear transcript and start a brand-new conversation (new session ID)"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Reset session
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs ml-auto"
            onClick={() => {
              setPreviewStarted(false);
              resetTranscript();
            }}
            data-testid="button-change-role"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Change role
          </Button>
        </div>
      )}
      {previewStarted && (
      <CardContent className="p-0" style={{ backgroundColor: theme.backgroundColor }}>
        {needsPreChat ? (
          <div className="p-4">
            <PreChatForm
              botName={chatbot.name}
              color={theme.primaryColor}
              apiKey={chatbot.apiKey}
              sessionId={previewSessionId}
              credentials="omit"
              onComplete={(c) => { setPreContact(c); setPreChatDone(true); }}
            />
          </div>
        ) : (
        <>
        <ScrollArea className="h-[400px] p-4" ref={scrollRef}>
          {/* Screen-reader live region (WCAG 4.1.3): announces typing status
              and each completed reply once; replaced, never appended.
              Toggle ON → always in the DOM; toggle OFF → no announcer node,
              except transiently while an essential error is announced. */}
          {shouldRenderAnnouncer(announceEnabled, srAnnouncement) && (
            <div className="sr-only" aria-live="polite" aria-atomic="true" role="status" data-testid="status-preview-announcer">
              {srAnnouncement}
            </div>
          )}
          <div className="space-y-4">
            {messages.map((msg, i) => {
              if (msg.role === "system") {
                return (
                  <div key={i} className="flex justify-center" data-testid={`message-system-${i}`}>
                    <span className="text-[11px] text-muted-foreground bg-muted/60 rounded-full px-3 py-1 max-w-[90%] text-center">
                      {msg.content}
                    </span>
                  </div>
                );
              }
              const isUser = msg.role === "user";
              const isAgent = msg.role === "agent";
              return (
                <div
                  key={i}
                  ref={(el) => {
                    if (i === lastUserIdx) latestUserMsgRef.current = el;
                    if (i === lastBotIdx) latestBotMsgRef.current = el;
                  }}
                  className={`flex gap-3 scroll-mt-2 scroll-mb-16 ${isUser ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${msg.role}-${i}`}
                >
                  {!isUser && (
                    <div
                      className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: isAgent ? "#0f766e" : theme.primaryColor, color: readableTextOn(isAgent ? "#0f766e" : theme.primaryColor) }}
                    >
                      {isAgent ? (
                        <Headset className="w-3.5 h-3.5" />
                      ) : (
                        <Bot className="w-3.5 h-3.5" />
                      )}
                    </div>
                  )}
                  <div
                    className={`rounded-md px-3 py-2 max-w-[80%] text-sm ${msg.role === "assistant" ? "chat-markdown" : ""}`}
                    style={{
                      backgroundColor: isUser ? theme.userBubble : theme.botBubble,
                      color: isUser ? theme.userBubbleText : theme.botBubbleText,
                      borderRadius: theme.borderRadius,
                    }}
                  >
                    {msg.role === "assistant" ? (
                      (() => {
                        // WCAG 3.1.2: per-message lang/dir so Urdu/Arabic
                        // replies read and render correctly.
                        const meta = getMessageLangMeta(msg.content, selectedLanguage);
                        return (
                          <div lang={meta.lang} dir={meta.dir}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
                            <KnowledgeCitations citations={msg.citations} />
                          </div>
                        );
                      })()
                    ) : (
                      msg.content
                    )}
                  </div>
                  {isUser && (
                    <div className="w-7 h-7 rounded-md bg-secondary flex items-center justify-center flex-shrink-0">
                      <User className="w-3.5 h-3.5" />
                    </div>
                  )}
                </div>
              );
            })}
            {isLoading && (
              <div className="flex gap-3 items-start" aria-hidden="true">
                <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.primaryColor, color: readableTextOn(theme.primaryColor) }}>
                  <Bot className="w-3.5 h-3.5" />
                </div>
                <div className="rounded-md px-3 py-2" style={{ backgroundColor: theme.botBubble }}>
                  <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" style={{ color: theme.botBubbleText }} />
                </div>
              </div>
            )}
            {showSuggestions && (() => {
              const grouped: Record<string, PredefinedQuestion[]> = {};
              for (const q of predefinedQuestions!) {
                const cat = q.category || "General";
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(q);
              }
              const categories = Object.keys(grouped);
              const hasMultipleCategories = categories.length > 1;

              return (
                <div className="mt-3 space-y-3">
                  <div className="flex items-center gap-2 px-1">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: theme.primaryColor }} />
                    <span className="text-xs font-medium" style={{ color: theme.botBubbleText, opacity: 0.7 }}>
                      Suggested questions
                    </span>
                  </div>
                  {categories.map((cat) => (
                    <div key={cat}>
                      {hasMultipleCategories && (
                        <div className="flex items-center gap-1.5 px-1 mb-1.5">
                          <HelpCircle className="w-3 h-3" style={{ color: theme.primaryColor, opacity: 0.6 }} />
                          <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: theme.botBubbleText, opacity: 0.5 }}>
                            {cat}
                          </span>
                        </div>
                      )}
                      <div className="space-y-1.5">
                        {grouped[cat].map((q) => (
                          <button
                            key={q.id}
                            onClick={() => sendMessage(q.question)}
                            className="group w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-left text-sm transition-all duration-200 hover:scale-[1.01] hover:shadow-md"
                            style={{
                              backgroundColor: theme.botBubble,
                              color: theme.botBubbleText,
                              border: `1px solid ${theme.primaryColor}22`,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = theme.primaryColor + "18";
                              e.currentTarget.style.borderColor = theme.primaryColor + "44";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = theme.botBubble;
                              e.currentTarget.style.borderColor = theme.primaryColor + "22";
                            }}
                            data-testid={`button-suggested-${q.id}`}
                          >
                            <MessageCircle className="w-4 h-4 flex-shrink-0 opacity-50 group-hover:opacity-80 transition-opacity" style={{ color: theme.primaryColor }} />
                            <span className="flex-1 leading-snug">{q.question}</span>
                            <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0" style={{ color: theme.primaryColor }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </ScrollArea>
        <div className="p-4 border-t space-y-2">
          {newReplyPending && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => scrollToLatest(latestBotMsgRef.current)}
                className="flex items-center gap-1.5 rounded-full bg-foreground text-background text-xs px-3 py-1.5 shadow hover:opacity-80 transition-opacity"
                data-testid="button-new-reply"
              >
                New reply <ArrowDown className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </div>
          )}
          {live?.status === "closed" && <button type="button" onClick={() => setLive(null)} data-testid="button-continue-ai">Continue with AI</button>}
          {liveActive ? (
            <div
              className="flex items-center justify-between gap-2 rounded-md bg-muted/60 px-3 py-2 text-xs"
              data-testid="banner-live-status"
            >
              <span className="flex items-center gap-1.5 font-medium" data-testid="text-live-status">
                <Headset className="w-3.5 h-3.5" style={{ color: theme.primaryColor }} />
                {live!.status === "queued"
                  ? `Waiting for an agent${live!.position ? ` · position ${live!.position}` : ""}…`
                  : `Connected${live!.operatorName ? ` to ${live!.operatorName}` : ""}`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={endLiveChat}
                data-testid="button-end-live-chat"
              >
                <PhoneOff className="w-3.5 h-3.5 mr-1" /> End chat
              </Button>
            </div>
          ) : (
            <div className="flex justify-end items-center gap-1.5">
              {departments.length > 0 && (
                <select
                  value={handoffDept}
                  onChange={(e) => setHandoffDept(e.target.value)}
                  className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
                  data-testid="select-handoff-department"
                >
                  <option value="">General queue</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2.5 text-xs gap-1.5"
                onClick={requestHandoff}
                disabled={handoffPending}
                data-testid="button-talk-to-person"
              >
                {handoffPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Headset className="w-3.5 h-3.5" />
                )}
                Talk to a person
              </Button>
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitInput();
              // Return focus to the input immediately — a mouse click on
              // Send moves focus to the button (which then disables while
              // the reply is pending), so without this the admin would have
              // to re-click the input to keep typing.
              inputRef.current?.focus();
            }}
            className="flex items-center gap-2"
          >
            <Select value={selectedLanguage || "auto"} onValueChange={(v) => setSelectedLanguage(v === "auto" ? "" : v)}>
              <SelectTrigger className="w-[130px] shrink-0" title={AUTO_LANGUAGE_HELP}
                aria-label="Choose the language the assistant replies in" data-testid="select-chat-language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHAT_LANGUAGES.map((l) => (
                  <SelectItem key={l.value || "auto"} value={l.value || "auto"}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={liveActive ? "Message the agent…" : "Type a message..."}
              disabled={live?.status === "closed"}
              aria-label={liveActive ? "Message to the live agent" : "Message to the assistant"}
              data-testid="input-chat-message"
            />
            <IconButton type="submit" label="Send message" disabled={live?.status === "closed" || !input.trim() || !!simulatedUserJsonError || (isLoading && !liveActive)} style={{ backgroundColor: theme.primaryColor, color: readableTextOn(theme.primaryColor) }} data-testid="button-send-message">
              <Send className="w-4 h-4" aria-hidden="true" />
            </IconButton>
          </form>
        </div>
        </>
        )}
      </CardContent>
      )}
    </Card>
  );
}
